import { Link } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Zap } from "lucide-react";

/**
 * Compact subscription/quota badge for the app header.
 * Shows current plan + a stacked progress meter for reports + downloads.
 * Clicking it opens the Pricing page.
 */
export default function UsageBadge() {
  const { data, loading } = useSubscription();
  if (loading || !data) return null;

  const { subscription, limits, used } = data;
  const planName = subscription?.plan_name || "Free";
  const planKey = subscription?.plan || "free";

  const reportsTotal = limits.reports || 0;
  const reportsUsed = used.reports || 0;
  const downloadsTotal = limits.downloads || 0;
  const downloadsUsed = used.downloads || 0;

  const reportsPct = reportsTotal > 0 ? Math.min(100, (reportsUsed / reportsTotal) * 100) : 0;
  const downloadsPct = downloadsTotal > 0 ? Math.min(100, (downloadsUsed / downloadsTotal) * 100) : 0;

  const reportsLow = reportsTotal > 0 && reportsUsed >= reportsTotal;
  const reportsWarn = reportsTotal > 0 && reportsUsed >= reportsTotal * 0.8 && !reportsLow;

  const planTone = {
    free: "border-[#262626] bg-[#1F1F1F] text-[#A3A3A3]",
    pro_monthly: "border-[#0A84FF]/40 bg-[#0A84FF]/10 text-[#0A84FF]",
    pro_yearly: "border-[#0A84FF]/40 bg-[#0A84FF]/10 text-[#0A84FF]",
    crm_elite: "border-[#FF9F0A]/40 bg-[#FF9F0A]/10 text-[#FF9F0A]",
  }[planKey] || "border-[#262626] bg-[#1F1F1F] text-[#A3A3A3]";

  return (
    <Link
      to="/pricing"
      data-testid="usage-badge"
      title={`${planName} · Reports ${reportsUsed}/${reportsTotal} · Downloads ${downloadsUsed}/${downloadsTotal}`}
      className="hidden md:flex items-center gap-3 h-9 px-3 rounded-sm border border-[#262626] bg-[#141414] hover:bg-[#1F1F1F] transition-colors group"
    >
      <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase ${planTone}`}>
        <Zap className="w-3 h-3" strokeWidth={2}/>
        {planName}
      </span>
      <div className="flex items-center gap-3 text-[10px] font-mono-data tracking-wider uppercase text-[#737373] group-hover:text-white transition-colors">
        <div className="flex items-center gap-1.5">
          <span>R</span>
          <div className="relative w-12 h-1 bg-[#262626] rounded-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 transition-all ${
                reportsLow ? "bg-[#FF453A]" : reportsWarn ? "bg-[#FF9F0A]" : "bg-[#0A84FF]"
              }`}
              style={{ width: `${reportsPct}%` }}
              data-testid="usage-bar-reports"
            />
          </div>
          <span className="tabular-nums">{reportsUsed}/{reportsTotal}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span>D</span>
          <div className="relative w-12 h-1 bg-[#262626] rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#0A84FF] transition-all"
              style={{ width: `${downloadsPct}%` }}
              data-testid="usage-bar-downloads"
            />
          </div>
          <span className="tabular-nums">{downloadsUsed}/{downloadsTotal}</span>
        </div>
      </div>
    </Link>
  );
}
